import { useState } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Loader2 } from 'lucide-react'
import type {AIMonitorConfig, Config, PromptServiceConfig} from '@/types/config'
import { defaultMonitorConfig } from '@/types/config'
import type { DisplaySession } from '@/types/session'
import { AVAILABLE_SESSIONS, displayToAPISession, apiToDisplaySession, isValidAPISession } from '@/types/session'
import { MonitorSettings } from './monitor'
import { billsApi } from "@/api/bills-api.ts"
import { useAuth } from '@/context/AuthContext';

interface ConfigFormProps {
  config?: Config
  onSubmit: (data: Partial<Config>) => Promise<void>
  isLoading: boolean
}

const DEFAULT_PROMPT: PromptServiceConfig = {
  Text: '',
  ServiceType: 'LLM_SONNET35' as const
}

const convertPromptToConfig = (value: any): PromptServiceConfig => {
  if (typeof value === 'string') {
    return {
      Text: value,
      ServiceType: 'LLM_SONNET35'
    }
  }
  if (value && typeof value === 'object') {
    return {
      Text: value.Text || '',
      ServiceType: value.ServiceType || 'LLM_SONNET35'
    }
  }
  return {...DEFAULT_PROMPT}
}

export function ConfigForm({ config, onSubmit, isLoading }: ConfigFormProps) {
  const { selectedOrganizationId, selectedWorkspaceId } = useAuth();
  const [activeSession, setActiveSession] = useState<DisplaySession>(
    config?.ActiveSession && isValidAPISession(config.ActiveSession) 
      ? apiToDisplaySession(config.ActiveSession) 
      : '2024 Regular Session'
  )
  const [prompts, setPrompts] = useState({
    LegislativeReview: convertPromptToConfig(config?.Prompts?.LegislativeReview),
    Opposition: convertPromptToConfig(config?.Prompts?.Opposition),
    Support: convertPromptToConfig(config?.Prompts?.Support),
    ShouldMonitor: convertPromptToConfig(config?.Prompts?.ShouldMonitor)
  })
  const [monitorConfig, setMonitorConfig] = useState(config?.AIMonitor ?? defaultMonitorConfig)
  const [thresholdChanges, setThresholdChanges] = useState<AIMonitorConfig['Thresholds'] | null>(null)
  const [isUpdatingBillStates, setIsUpdatingBillStates] = useState(false)

  const handleMonitorUpdate = (updates: Partial<AIMonitorConfig>) => {
    // Log monitor update to track changes, particularly for PromptText
    console.log('Monitor config update received:', updates);
    
    setMonitorConfig(prev => {
      const newConfig = { ...prev, ...updates };
      console.log('Updated monitor config:', newConfig);
      return newConfig;
    })

    // If this update includes threshold changes, track them
    if (updates.Thresholds) {
      setThresholdChanges(updates.Thresholds)
    }
    
    // Explicitly log PromptText changes for debugging
    if ('PromptText' in updates) {
      console.log('PromptText updated to:', updates.PromptText);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isLoading) {
      try {
        // Log specific focus on the Monitoring Prompt to verify it's being included
        console.log('Submitting config with values:', {
          ActiveSession: displayToAPISession(activeSession),
          Prompts: { ...prompts },
          AIMonitor: monitorConfig,
          MonitoringPrompt: monitorConfig.PromptConfig?.PromptText // Correct nested property
        });
        
        // First, update the configuration and wait for it to complete
        await onSubmit({
          ActiveSession: displayToAPISession(activeSession),
          Prompts: { ...prompts },
          AIMonitor: monitorConfig
        })

        // After config update is complete, check for threshold changes and update states
        if (thresholdChanges && selectedOrganizationId && selectedWorkspaceId) {
          setIsUpdatingBillStates(true)
          try {
            await billsApi.updateAllBillStates(selectedOrganizationId, selectedWorkspaceId)
          } finally {
            setIsUpdatingBillStates(false)
            setThresholdChanges(null)
          }
        }
      } catch (error) {
        console.error('Error during submission:', error)
      }
    }
  }

  const updatePrompt = (key: keyof typeof prompts, updates: Partial<PromptServiceConfig>) => {
    setPrompts(prev => ({
      ...prev,
      [key]: { ...prev[key], ...updates }
    }))
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Legislative Session</CardTitle>
          <CardDescription>Configure active session for this workspace (bill sync is managed in admin settings)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Active Session</Label>
            <Select 
              value={activeSession} 
              onValueChange={setActiveSession as (value: string) => void}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select session" />
              </SelectTrigger>
              <SelectContent>
                {AVAILABLE_SESSIONS.map(session => (
                  <SelectItem key={session} value={session}>{session}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Sets the active legislative session for this workspace.
            </p>
          </div>
        </CardContent>
      </Card>

      <MonitorSettings 
        config={monitorConfig}
        onUpdate={handleMonitorUpdate}
        isLoading={isLoading}
        billReviewPrompt={prompts.LegislativeReview}
        oppositionPrompt={prompts.Opposition}
        supportPrompt={prompts.Support}
        onPromptUpdate={updatePrompt}
      />

      <div className="flex flex-col space-y-2 mb-8">
        {isUpdatingBillStates && (
          <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 p-2 rounded border border-blue-200 self-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Updating bill states...</span>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button
            type="submit"
            disabled={isLoading || isUpdatingBillStates}
            className="bg-blue-700 hover:bg-blue-800 text-white font-medium"
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  )
}